import { useGenerations, useMe, useProducts } from '@studio/api-client';
import { Button, Card, CardBody, CardDescription, CardHeader, CardTitle, Chip } from '@studio/ui';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BrandDnaModal } from '../components/BrandDnaModal';
import { humanizeError } from '../lib/error-copy';
import { getWixMediaSafeUrl } from '../lib/wix-cdn';
import { track } from '../observability';

interface Tile {
  uid: string;
  url: string;
  title: string;
  subtitle: string;
  kind: 'live' | 'draft';
  platform?: string;
  task?: string;
}

/**
 * Gallery — unified image surface. Merges product primary images (`live`)
 * with Studio outputs (`draft`). Most recent generations first; live
 * products grouped after.
 *
 * Spec lineage: docs/13-ui-vision.md (legacy) — Surface 2.
 */
export function GalleryPage() {
  const me = useMe();
  const enabled = me.isSuccess && me.data.workspaceId !== null;
  const products = useProducts({ enabled });
  const generations = useGenerations();
  const [brandOpen, setBrandOpen] = useState(false);

  return (
    <AppShell activeTab="gallery" onOpenBrand={() => setBrandOpen(true)}>
      <header className="mb-8 flex items-end justify-between">
        <div>
          <p className="eyebrow mb-2">Surface 2 · Library</p>
          <h1 className="display text-display-xl">Your gallery</h1>
          <p className="mt-1 text-sm text-fg-3">
            Studio outputs alongside the live product images. Drafts persist across sessions.
          </p>
        </div>
      </header>

      <GalleryBody me={me} products={products} generations={generations} />
      <BrandDnaModal open={brandOpen} onOpenChange={setBrandOpen} />
    </AppShell>
  );
}

interface GalleryBodyProps {
  me: ReturnType<typeof useMe>;
  products: ReturnType<typeof useProducts>;
  generations: ReturnType<typeof useGenerations>;
}

function GalleryBody({ me, products, generations }: GalleryBodyProps) {
  if (me.isPending) return <GallerySkeletons />;
  if (me.error) return <Err message={humanizeError(me.error)} />;
  if (me.data && me.data.workspaceId === null) {
    return (
      <Empty
        title="Workspace not ready"
        body="Sign in again to refresh the JWT after the workspace bootstrap."
      />
    );
  }
  if (products.isPending || generations.isPending) return <GallerySkeletons />;
  if (products.error) return <Err message={humanizeError(products.error)} />;
  if (generations.error) return <Err message={humanizeError(generations.error)} />;

  const draftTiles: Tile[] = (generations.data?.items ?? [])
    .filter((g) => g.outputImageUrl)
    .map((g) => ({
      uid: `gen:${g.id}`,
      url: g.outputImageUrl as string,
      title: g.originalPrompt.slice(0, 60) + (g.originalPrompt.length > 60 ? '…' : ''),
      subtitle: `${g.task} · job ${g.id}`,
      kind: 'draft' as const,
      task: g.task,
    }));

  const liveTiles: Tile[] = (products.data?.items ?? [])
    .filter((p) => p.primaryImageUrl)
    .map((p) => ({
      uid: `${p.uid}:primary`,
      url: p.primaryImageUrl as string,
      title: p.title,
      subtitle: 'Primary product image',
      kind: 'live' as const,
      platform: p.platformId,
    }));

  const tiles = [...draftTiles, ...liveTiles];

  return <GalleryGrid tiles={tiles} />;
}

function GalleryGrid({ tiles }: { tiles: Tile[] }) {
  // Fire `gallery.viewed` exactly once per mount with the tile count at
  // first render. A ref guard prevents re-emit if React re-runs the
  // effect (StrictMode double-invoke) — count drift on re-fetch is
  // already covered by the pageview event.
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    track('gallery.viewed', { tileCount: tiles.length });
  }, [tiles.length]);

  if (tiles.length === 0) {
    return (
      <Empty
        title="No images yet"
        body="Connect a platform to import your catalog — Studio drafts and live product photos both land here."
        cta={{ to: '/connections', label: 'Connect a platform' }}
      />
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {tiles.map((tile) => (
        <Card key={tile.uid} interactive className="overflow-hidden">
          <img
            src={getWixMediaSafeUrl(tile.url)}
            alt={tile.title}
            loading="lazy"
            decoding="async"
            className="aspect-square w-full bg-surface-2 object-cover"
          />
          <CardHeader>
            <CardTitle className="line-clamp-1 text-sm">{tile.title}</CardTitle>
            <CardDescription className="flex items-center gap-2 text-xs">
              <Chip size="xs" variant="soft" tone={tile.kind === 'draft' ? 'accent' : 'neutral'}>
                {tile.kind}
              </Chip>
              {tile.platform ? (
                <Chip size="xs" variant="soft" tone="neutral">
                  {tile.platform}
                </Chip>
              ) : null}
              {tile.task ? (
                <Chip size="xs" variant="solid" tone="neutral">
                  {tile.task}
                </Chip>
              ) : null}
            </CardDescription>
          </CardHeader>
          <CardBody className="pt-0 text-xs text-fg-3">{tile.subtitle}</CardBody>
        </Card>
      ))}
    </div>
  );
}

function GallerySkeletons() {
  return (
    <div
      className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"
      aria-busy="true"
      aria-label="Loading gallery"
    >
      {Array.from({ length: 8 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: pure presentational skeleton, order is fixed
        <div key={i} className="overflow-hidden rounded-lg border border-border bg-surface">
          <div className="aspect-square w-full animate-pulse bg-surface-2" />
          <div className="space-y-2 p-4">
            <div className="h-3 w-2/3 animate-pulse rounded bg-surface-2" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-surface-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Err({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-danger/40 bg-danger/10 p-5 text-sm text-danger">
      {message}
    </div>
  );
}

function Empty({
  title,
  body,
  cta,
}: {
  title: string;
  body: string;
  cta?: { to: string; label: string };
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-8 text-center">
      <h2 className="display text-display-md">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-fg-2">{body}</p>
      {cta ? (
        <div className="mt-5">
          <Button asChild variant="primary">
            <Link to={cta.to}>{cta.label}</Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
